import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/SupabaseClient";

/* ---------- Types ---------- */
interface Profile {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
    role: string;
}

interface Post {
    id: number;
    author_id: string | null;
    content: string;
    media_url: string | null;
    video_url: string | null; // ✅ New
    created_at: string;
    author?: Omit<Profile, "role"> | null;
    liked_by_me?: boolean;
    like_count?: number;
}

/* ---------- Helpers ---------- */
const cx = (...classes: (string | undefined | null | false)[]) =>
    classes.filter(Boolean).join(" ");

const timeAgo = (date: string | Date) => {
    const diff = Date.now() - new Date(date).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
};

/* ---------- Avatar ---------- */
const Avatar: React.FC<{ profile?: Partial<Profile> | null; size?: number }> = ({
    profile,
    size = 36,
}) => {
    const name = profile?.full_name || "User";
    const initials = useMemo(
        () =>
            name
                .split(" ")
                .map((p) => p[0])
                .slice(0, 2)
                .join("")
                .toUpperCase(),
        [name]
    );
    return (
        <div
            className="inline-flex items-center justify-center rounded-full bg-neutral-800 text-neutral-200 overflow-hidden"
            style={{ width: size, height: size }}
        >
            {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt={name} className="w-full h-full object-cover" />
            ) : (
                <span className="text-xs font-semibold">{initials}</span>
            )}
        </div>
    );
};

/* ---------- Upload Helper ---------- */
const uploadMedia = async (file: File): Promise<string> => {
    const ext = file.name.split(".").pop();
    const path = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage
        .from("posts-media")
        .upload(path, file, { cacheControl: "3600", upsert: false });
    if (error) throw error;

    const { data } = supabase.storage.from("posts-media").getPublicUrl(path);
    return data.publicUrl;
};

/* ---------- Comment Box ---------- */
const CommentBox: React.FC<{ onSubmit: (text: string) => Promise<void> }> = ({
    onSubmit,
}) => {
    const [val, setVal] = useState("");
    return (
        <div className="flex gap-2 mt-3">
            <input
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder="Write a comment…"
                className="flex-1 bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-sm text-neutral-100 outline-none"
            />
            <button
                onClick={async () => {
                    if (!val.trim()) return;
                    await onSubmit(val.trim());
                    setVal("");
                }}
                className="px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm hover:bg-indigo-500"
            >
                Post
            </button>
        </div>
    );
};

/* ---------- Post Card ---------- */
const PostCard: React.FC<{
    post: Post;
    me?: Profile | null;
    onLike: (id: number, liked: boolean) => Promise<void>;
    onComment: (id: number, text: string) => Promise<void>;
}> = ({ post, me, onLike, onComment }) => {
    const [openComments, setOpenComments] = useState(false);
    const [liked, setLiked] = useState(post.liked_by_me || false);

    const renderMedia = () => {
        // ✅ YouTube embed
        if (post.video_url) {
            const match = post.video_url.match(
                /(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]+)/
            );
            if (match) {
                const videoId = match[1];
                return (
                    <div className="mt-2 aspect-video rounded-xl overflow-hidden">
                        <iframe
                            className="w-full h-full"
                            src={`https://www.youtube.com/embed/${videoId}`}
                            title="YouTube video"
                            allowFullScreen
                        ></iframe>
                    </div>
                );
            }
        }

        if (post.media_url) {
            const isVideo = /\.(mp4|webm|ogg)$/i.test(post.media_url);
            return isVideo ? (
                <video
                    controls
                    src={post.media_url}
                    className="w-full max-h-[440px] object-cover rounded-none"
                />
            ) : (
                <img
                    src={post.media_url}
                    alt="attachment"
                    className="w-full max-h-[440px] object-cover rounded-none"
                />
            );
        }

        return null;
    };

    return (
        <div className="pb-3 border-b border-neutral-800">
            <div className="flex items-center gap-3 px-2 py-2">
                <Avatar profile={post.author} />
                <div>
                    <div className="font-semibold text-neutral-100">
                        {post.author?.full_name || "SecureID Team"}
                    </div>
                    <div className="text-xs text-neutral-500">{timeAgo(post.created_at)}</div>
                </div>
            </div>

            <div className="px-2 text-neutral-200 whitespace-pre-wrap">{post.content}</div>

            {renderMedia()}

            <div className="px-2 mt-2 text-sm text-neutral-400">
                {post.like_count ? `${post.like_count} likes` : "Be the first to like this"}
            </div>

            <div className="flex items-center justify-around px-2 py-2 text-sm text-neutral-400">
                <button
                    disabled={!me}
                    onClick={() => {
                        if (!me) return;
                        setLiked(!liked);
                        onLike(post.id, liked);
                    }}
                    className={cx(
                        "flex items-center gap-1 font-medium hover:text-neutral-100 disabled:opacity-50",
                        liked && "text-indigo-400"
                    )}
                >
                    ♥ Like
                </button>

                <button
                    disabled={!me}
                    onClick={() => me && setOpenComments((v) => !v)}
                    className="flex items-center gap-1 font-medium hover:text-neutral-100 disabled:opacity-50"
                >
                    💬 Comment
                </button>

                <button
                    disabled={!me}
                    onClick={() =>
                        me && navigator.clipboard.writeText(`${window.location.origin}/#post-${post.id}`)
                    }
                    className="flex items-center gap-1 font-medium hover:text-neutral-100 disabled:opacity-50"
                >
                    🔗 Share
                </button>
            </div>

            {openComments && me && (
                <div className="px-2 pb-2">
                    <CommentBox onSubmit={(text) => onComment(post.id, text)} />
                </div>
            )}
        </div>
    );
};

/* ---------- Post Composer ---------- */
const PostComposer: React.FC<{
    me?: Profile | null;
    onPost: (content: string, file?: File | null, videoUrl?: string | null) => Promise<void>;
}> = ({ me, onPost }) => {
    const [val, setVal] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [videoUrl, setVideoUrl] = useState("");
    const fileInput = useRef<HTMLInputElement | null>(null);

    if (!me) return null;

    return (
        <div className="mb-4 border-b border-neutral-800 pb-4">
            <div className="flex gap-3 items-center">
                <Avatar profile={me} />
                <input
                    type="text"
                    placeholder="Start a post..."
                    className="flex-1 bg-neutral-950 text-neutral-200 text-sm border border-neutral-800 rounded-full px-4 py-2 outline-none focus:ring-1 focus:ring-indigo-500"
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                />
                <input
                    ref={fileInput}
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    className="hidden"
                />
                <button
                    onClick={() => fileInput.current?.click()}
                    title="Add media"
                    className="px-3 text-neutral-400 hover:text-white"
                >
                    📷
                </button>
            </div>

            {/* ✅ YouTube URL input */}
            <input
                type="url"
                placeholder="Paste YouTube link (optional)"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                className="mt-2 w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 text-sm text-neutral-200 outline-none focus:ring-1 focus:ring-indigo-500"
            />

            <div className="mt-3 flex justify-end">
                <button
                    disabled={!val.trim() && !file && !videoUrl.trim()}
                    onClick={async () => {
                        await onPost(val, file, videoUrl.trim() || null);
                        setVal("");
                        setFile(null);
                        setVideoUrl("");
                        if (fileInput.current) fileInput.current.value = "";
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${val.trim() || file || videoUrl.trim()
                            ? "bg-indigo-500 hover:bg-indigo-400 text-white"
                            : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                        }`}
                >
                    Post
                </button>
            </div>
        </div>
    );
};

/* ---------- Main Component ---------- */
const SocialWall: React.FC = () => {
    const [me, setMe] = useState<Profile | null>(null);
    const [posts, setPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);
    const [profileOpen, setProfileOpen] = useState(false);

    const openAuthModal = (): void => window.dispatchEvent(new Event("open-social-login"));

    useEffect(() => {
        const init = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                const { data } = await supabase.from("profiles").select("*").eq("id", session.user.id).single();
                setMe(data);
            }
        };
        init();
        const { data: listener } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
            if (newSession?.user) {
                const { data } = await supabase.from("profiles").select("*").eq("id", newSession.user.id).single();
                setMe(data);
            } else setMe(null);
        });
        return () => listener.subscription.unsubscribe();
    }, []);

    const loadPosts = async (): Promise<void> => {
        setLoading(true);
        try {
            // ✅ Step 1. Fetch posts (safer query)
            const { data: postsData, error: postsError } = await supabase
                .from("posts")
                .select("id, author_id, content, media_url, video_url, created_at")
                .order("created_at", { ascending: false });

            if (postsError) {
                console.error("❌ Error loading posts:", postsError.message);
                setPosts([]);
                return;
            }

            if (!postsData || postsData.length === 0) {
                setPosts([]);
                return;
            }

            // ✅ Step 2. Collect author IDs safely
            const authorIds = postsData
                .map((p) => p.author_id)
                .filter((id): id is string => !!id);

            // ✅ Step 3. Fetch authors only if needed
            let profiles: Profile[] = [];
            if (authorIds.length > 0) {
                const { data: profilesData, error: profilesError } = await supabase
                    .from("profiles")
                    .select("id, full_name, avatar_url")
                    .in("id", authorIds);

                if (profilesError) {
                    console.warn("⚠️ Warning loading profiles:", profilesError.message);
                } else if (profilesData) {
                    profiles = profilesData as Profile[];
                }
            }

            // ✅ Step 4. Merge results safely
            const merged = postsData.map((p) => ({
                ...p,
                author:
                    profiles.find((a) => a.id === p.author_id) || {
                        id: "",
                        full_name: "SecureID Team",
                        avatar_url: "https://api.dicebear.com/7.x/identicon/svg?seed=SecureID",
                    },
            }));

            setPosts(merged);
        } catch (err) {
            console.error("🚨 Unexpected error in loadPosts:", err);
        } finally {
            // ✅ Always stop loading spinner
            setLoading(false);
        }
    };


    useEffect(() => {
        loadPosts();
    }, []);

    const handlePost = async (content: string, file?: File | null, videoUrl?: string | null) => {
        if (!me) return openAuthModal();
        try {
            let media_url: string | null = null;
            if (file) media_url = await uploadMedia(file);
            await supabase
                .from("posts")
                .insert({ content, author_id: me.id, media_url, video_url: videoUrl || null });
            await loadPosts();
        } catch (err) {
            console.error("❌ handlePost failed:", err);
        }
    };

    const handleLike = async (postId: number, liked: boolean) => {
        if (!me) return openAuthModal();
        if (liked)
            await supabase.from("likes").delete().eq("post_id", postId).eq("user_id", me.id);
        else await supabase.from("likes").insert({ post_id: postId, user_id: me.id });
        await loadPosts();
    };

    const handleComment = async (postId: number, text: string) => {
        if (!me) return openAuthModal();
        await supabase.from("comments").insert({ post_id: postId, content: text, author_id: me.id });
        await loadPosts();
    };

    return (
        <section className="bg-neutral-950 min-h-screen text-neutral-100">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(600px,700px)_1fr] gap-6 max-w-[1400px] mx-auto px-2 md:px-6 py-8">
                {/* Left Sidebar */}
                <aside className="hidden lg:block text-sm text-neutral-400">
                    <div className="sticky top-20 space-y-3">
                        <div className="bg-neutral-900/40 border border-neutral-800 rounded-xl p-4">
                            {me ? (
                                <>
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={
                                                me.avatar_url ||
                                                "https://api.dicebear.com/7.x/identicon/svg?seed=SecureID"
                                            }
                                            alt={me.full_name || "User"}
                                            className="w-10 h-10 rounded-full border border-neutral-700"
                                        />
                                        <div>
                                            <p className="font-semibold text-neutral-200">{me.full_name}</p>
                                            <p className="text-xs text-neutral-500">{me.role || "Member"}</p>
                                        </div>
                                    </div>
                                    <div className="mt-3 text-xs text-neutral-500">
                                        <p>🕒 Joined: {new Date().toLocaleDateString()}</p>
                                        <p>📍 Miami, FL</p>
                                    </div>
                                    <button
                                        onClick={() => supabase.auth.signOut()}
                                        className="mt-4 w-full py-1.5 rounded-lg bg-neutral-800 text-neutral-200 hover:bg-neutral-700 transition text-sm"
                                    >
                                        Sign out
                                    </button>
                                </>
                            ) : (
                                <>
                                    <p className="font-semibold text-neutral-300">Profile Widget</p>
                                    <p className="text-xs mt-2 text-neutral-500">
                                        Sign in to view your profile and stats.
                                    </p>
                                    <button
                                        onClick={openAuthModal}
                                        className="mt-3 w-full py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 text-sm"
                                    >
                                        Sign in
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </aside>

                {/* Center Feed */}
                <main className="socialwall space-y-3 lg:max-h-[calc(100vh-80px)] lg:overflow-y-auto px-1">
                    <div className="flex items-center justify-between mb-2 sticky top-0 bg-neutral-950/90 backdrop-blur-sm py-2 z-10">
                        <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-neutral-100">
                                Community Updates
                            </h2>
                            <p className="text-neutral-400 text-sm">
                                News and conversations from SecureID Technologies LLC.
                            </p>
                        </div>

                        {me ? (
                            <div className="hidden sm:flex items-center gap-3">
                                <img
                                    src={
                                        me.avatar_url ||
                                        "https://api.dicebear.com/7.x/identicon/svg?seed=SecureID"
                                    }
                                    alt={me.full_name || "User"}
                                    className="w-8 h-8 rounded-full border border-neutral-700"
                                />
                                <span className="text-neutral-300 text-sm font-medium">{me.full_name}</span>
                                <button
                                    onClick={() => supabase.auth.signOut()}
                                    className="px-3 py-1.5 rounded-lg bg-neutral-900 border border-neutral-800 text-neutral-200 text-sm hover:bg-neutral-800"
                                >
                                    Sign out
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={openAuthModal}
                                className="px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 text-neutral-200 text-sm hover:bg-neutral-800"
                            >
                                Sign in
                            </button>
                        )}
                    </div>

                    <PostComposer me={me} onPost={handlePost} />

                    <div className="space-y-2 pb-10">
                        {loading ? (
                            <div className="animate-pulse text-neutral-500">Loading posts…</div>
                        ) : posts.length ? (
                            posts.map((p) => (
                                <PostCard
                                    key={p.id}
                                    post={p}
                                    me={me}
                                    onLike={handleLike}
                                    onComment={handleComment}
                                />
                            ))
                        ) : (
                            <div className="text-neutral-500">
                                No posts yet.{" "}
                                {me && (me.role === "admin" || me.role === "editor")
                                    ? "Share your first update!"
                                    : "Check back soon."}
                            </div>
                        )}
                    </div>
                </main>

                {/* Right Sidebar */}
                <aside className="hidden lg:block text-sm text-neutral-400">
                    <div className="sticky top-20 space-y-3">
                        <div className="bg-neutral-900/40 border border-neutral-800 rounded-xl p-4">
                            <p className="font-semibold text-neutral-300">Trending</p>
                            <ul className="text-xs mt-2 space-y-1 text-neutral-500">
                                <li>AI and Identity Management</li>
                                <li>New FDLE Updates</li>
                                <li>SecureID Launch Events</li>
                            </ul>
                        </div>
                    </div>
                </aside>
            </div>

            {/* Mobile Profile Drawer */}
            {profileOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-end">
                    <div className="bg-neutral-900 w-80 h-full p-4 border-l border-neutral-800 flex flex-col animate-slideIn">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-neutral-100">My Profile</h3>
                            <button
                                onClick={() => setProfileOpen(false)}
                                className="text-neutral-400 hover:text-white text-xl"
                            >
                                ✕
                            </button>
                        </div>

                        {me ? (
                            <>
                                <div className="flex items-center gap-3 mb-3">
                                    <img
                                        src={
                                            me.avatar_url ||
                                            "https://api.dicebear.com/7.x/identicon/svg?seed=SecureID"
                                        }
                                        alt={me.full_name || "User"}
                                        className="w-12 h-12 rounded-full border border-neutral-700"
                                    />
                                    <div>
                                        <p className="font-semibold text-neutral-200">{me.full_name}</p>
                                        <p className="text-xs text-neutral-500">{me.role || "Member"}</p>
                                    </div>
                                </div>

                                <div className="text-xs text-neutral-500 mb-4">
                                    <p>🕒 Joined: {new Date().toLocaleDateString()}</p>
                                    <p>📍 Miami, FL</p>
                                </div>

                                <button
                                    onClick={() => supabase.auth.signOut()}
                                    className="mt-auto w-full py-2 rounded-lg bg-neutral-800 text-neutral-200 hover:bg-neutral-700 transition text-sm"
                                >
                                    Sign out
                                </button>
                            </>
                        ) : (
                            <>
                                <p className="text-neutral-400 mb-4">Sign in to view your profile.</p>
                                <button
                                    onClick={openAuthModal}
                                    className="w-full py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 text-sm"
                                >
                                    Sign in
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

export default SocialWall;
